import axios from "axios";
import {
  forceLogout,
  isAccessTokenExpired,
} from "../template/utils/authSession";
import { classifyUserSearchQuery } from '../template/utils/phoneSearch';

const buildTimeBaseURL = process.env.REACT_APP_API_BASE_URL;

export const axiosInstance = axios.create({
  baseURL: buildTimeBaseURL,
  timeout: 30000,
  headers: { authorization: "Bearer " + localStorage.getItem("token") },
});

axios.defaults.baseURL = buildTimeBaseURL;

/**
 * Set API base URL at runtime (e.g. from /config.json).
 * This keeps dev and production admin deployments isolated.
 */
export function setApiBaseUrl(url) {
  if (url && typeof url === "string") {
    const base = url.replace(/\/$/, "");
    axios.defaults.baseURL = base;
    axiosInstance.defaults.baseURL = base;
  }
}

export function getApiBaseUrl() {
  return axios.defaults.baseURL || buildTimeBaseURL;
}

function attachAuthInterceptors(client) {
  client.interceptors.request.use(
    (config) => {
      const url = String(config.url || "");
      const isPublicAdminLogin = url.includes("/api/public/admin/login");
      const token = localStorage.getItem("token");

      if (!isPublicAdminLogin && token && isAccessTokenExpired(token)) {
        forceLogout("token_expired");
        return Promise.reject(new Error("Session expired"));
      }

      if (token) {
        config.headers = config.headers || {};
        config.headers.authorization = "Bearer " + token;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = String(error?.config?.url || "");
      const isPublicAdminLogin = url.includes("/api/public/admin/login");
      // Expired / missing auth → backend returns 401 for /api/private/**
      if (status === 401 && !isPublicAdminLogin) {
        forceLogout("api_unauthorized");
      }
      return Promise.reject(error);
    }
  );
}

attachAuthInterceptors(axios);
attachAuthInterceptors(axiosInstance);

export function signIn(login, password) {
  return axios.post("/api/public/admin/login", {
    login,
    password,
  });
}

export function getUsersList(page, pageSize) {
  return axios.get("/api/private/admin/allUsers", {
    params: {
      page,
      ...(pageSize != null && { pageSize }),
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function FindUsers(query) {
  const classified = classifyUserSearchQuery(query);

  if (classified.type === 'id') {
    const userId = classified.value;
    return axios.get(`/api/private/admin/findUserById`, {
      params: { userId, id: userId },
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    });
  }

  if (classified.type === 'email') {
    return axios.get(`/api/private/admin/findUserByEmail`, {
      params: { email: classified.value },
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    });
  }

  const phoneNumber = classified.type === 'phone' ? classified.value : String(query ?? '').trim();
  const encoded = encodeURIComponent(phoneNumber);
  return axios.get(`/api/private/admin/findUserByPhoneNumber${encoded}`, {
    params: { phoneNumber },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function getRents(page) {
  return axios.get("/api/private/admin/allRents", {
    params: {
      page,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

/**
 * Unified rents search (page/pageSize/sort + optional filters AND).
 * @param {object} params
 * @param {number} [params.page]
 * @param {number} [params.pageSize]
 * @param {string} [params.sort] e.g. "startDate,desc"
 * @param {string} [params.stationNumber]
 * @param {string} [params.stationId]
 * @param {string} [params.phone]
 * @param {string} [params.rentId]
 * @param {string} [params.powerBankId]
 * @param {string} [params.startDate] yyyy-MM-dd'T'HH:mm:ss.SSS
 * @param {string} [params.endDate]
 * @param {string} [params.status]
 */
export function searchRents(params = {}) {
  const {
    page = 0,
    pageSize = 10,
    sort,
    stationNumber,
    stationId,
    phone,
    rentId,
    powerBankId,
    startDate,
    endDate,
    status,
  } = params;
  return axiosInstance.get("/api/private/admin/rents", {
    timeout: 120000,
    params: {
      page,
      pageSize,
      ...(sort ? { sort } : {}),
      ...(stationNumber ? { stationNumber } : {}),
      ...(stationId ? { stationId } : {}),
      ...(phone ? { phone } : {}),
      ...(rentId ? { rentId } : {}),
      ...(powerBankId ? { powerBankId } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
      ...(status ? { status } : {}),
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

/** Overview KPI cards: period = day | week | month (today / last 7 days / last 30 days). */
export function getDashboardOverviewKpi(period = "month") {
  return axios.get("/api/private/admin/dashboard/overview-kpi", {
    params: { period },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

/** @deprecated use getDashboardOverviewKpi */
export function getDashboardCounts() {
  return getDashboardOverviewKpi("month");
}

export function lockUser(phoneNumber, lock) {
  return axios.get("/api/private/admin/lockUser", {
    params: {
      phoneNumber,
      lock: lock,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function getUserRent(phoneNumber) {
  const str = String(phoneNumber ?? '').trim();
  if (!str) {
    return Promise.reject(new Error("Enter phone number, station number (4 digits), or rent ID"));
  }

  const digits = str.replace(/\D/g, '');
  const digitsLen = digits.length;
  // Station search can return 1000+ fat rent objects (~MBs); allow a longer wait.
  const rentSearchConfig = {
    timeout: 120000,
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  };

  // Rent ID: 24 hex-символа (ObjectId) — единственный случай, когда идём в findRentById
  if (/^[0-9a-fA-F]{24}$/.test(str)) {
    return axiosInstance.get(
      `/api/private/admin/findRentById?${"rentId=" + encodeURIComponent(str)}`,
      rentSearchConfig,
    );
  }

  // 4 цифры → номер станции
  if (digitsLen === 4) {
    return axiosInstance.get(
      `/api/private/admin/findRentByStationNumber?${"stationNumber=" + encodeURIComponent(digits)}`,
      rentSearchConfig,
    );
  }

  // Телефон: 4–15 цифр (E.164: РФ, Беларусь, Эстония и др.). В путь передаём только цифры.
  // При 404 пробуем вариант с "+" — в БД номера могут храниться как "+79163323256".
  if (digitsLen >= 4 && digitsLen <= 15) {
    return axiosInstance
      .get(`/api/private/admin/userRents${encodeURIComponent(digits)}`, rentSearchConfig)
      .catch((err) => {
        if (err?.response?.status === 404 && !digits.startsWith('+')) {
          return axiosInstance.get(
            `/api/private/admin/userRents${encodeURIComponent('+' + digits)}`,
            rentSearchConfig,
          );
        }
        throw err;
      });
  }

  return Promise.reject(new Error("Enter phone number, station number (4 digits), or rent ID"));
}

export function GetStations(page, pageSize) {
  return axios.get("/api/private/admin/allStations", {
    params: {
      page,
      ...(pageSize != null && { pageSize }),
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function DeleteStation(stationNumber) {
  return axios.get("/api/private/admin/deleteStationByNumber", {
    params: {
      stationNumber,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function DeleteUsersApi(phoneNumber) {
  return axios.get("/api/private/admin/deleteUser", {
    params: {
      phoneNumber,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function lockStation(stationNumber, lock) {
  return axios.get("/api/private/admin/lockStation", {
    params: {
      stationNumber,
      lock: lock,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function setStationPlacement(stationNumber, placement) {
  return axios.get("/api/private/admin/setStationPlacement", {
    params: {
      stationNumber,
      placement,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function addStation(
  tariffPlan,
  address,
  latitude,
  longitude,
  numberSlots,
  owner,
  stationID,
  stationNumber,
  workTime,
  additionalAddress
) {
  const timezone = new Date().toTimeString().slice(9).slice(0, 8);
  return axios.post("/api/private/admin/addOrUpdateStation", {
    address,
    latitude,
    longitude,
    numberSlots,
    owner,
    stationID,
    stationNumber,
    workTime,
    tariffPlan,
    timezone,
    additionalAddress,
  }, {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function AddImage(image, stationNumber, captions) {
  const formData = new FormData();
  const files = Array.isArray(image) ? image : [image];
  const types = Array.isArray(captions) ? captions : [];
  files.forEach((file, index) => {
    formData.append("multipartFiles", file);
    const slotType = types[index];
    if (slotType != null && String(slotType).trim() !== "") {
      // Prefer explicit `types` (BE maps streetView|entrance|landmark → _1|_2|_3).
      formData.append("types", String(slotType).trim());
      formData.append(`type_${index}`, String(slotType).trim());
      formData.append(`comment_${index}`, String(slotType).trim());
    }
  });
  // Single-file convenience: also send `type` query/form field.
  if (files.length === 1 && types[0]) {
    formData.append("type", String(types[0]).trim());
  }
  return axios.post("/api/private/admin/addStationImage", formData, {
    params: {
      stationNumber,
      ...(files.length === 1 && types[0] ? { type: String(types[0]).trim() } : {}),
    },
    timeout: 120000,
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
      "Content-Type": "multipart/form-data",
    },
  });
}

export function findStations(stationNumber) {
  const Filter = Object.values(stationNumber);
  if (Filter.length === 4) {
    return axios.get("/api/private/admin/stationByNumber", {
      params: {
        stationNumber,
      },
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    });
  } else {
    let stationID = stationNumber;
    return axios.get("/api/private/admin/FindStationByID", {
      params: {
        stationID,
      },
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    });
  }
}

/** Prefer stationByNumber for numeric/terminal serial lookups (service tickets). */
export function findStationByNumber(stationNumber) {
  return axios.get("/api/private/admin/stationByNumber", {
    params: { stationNumber },
  });
}

//
// export function ChechStations(stationNumber) {
//     return axios.get('/api/private/admin/stationByNumber', {
//       params: {
//         stationNumber,
//       },
//       headers: {
//         authorization: 'Bearer ' + localStorage.getItem('token'),
//       }
//     })
// }

export function getStationImg(stationNumber) {
  return axios.get(
    `/api/private/admin/stationByNumber?${"stationNumber=" + stationNumber}`,
    {
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    }
  );
}

export function getStationAllImg(stationNumber) {
  return axios.get(
    `/api/private/admin/getAdditionalImages?${
      "stationNumber=" + stationNumber
    }`,
    {
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    }
  );
}

export function getSupport() {
  return axios.get("/api/private/admin/getSupportText", {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function getAgreement() {
  return axios.get(`/api/private/admin/getUserAgreements`, {});
}

export function GetAllGroup() {
  return axios.get("/api/private/admin/getAllGroupTariffs", {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function addSupport(
  locationForSupport,
  supportEmail,
  supportPhoneNumber
) {
  return axios.post("/api/private/admin/addAndUpdateSupportText", {
    locationForSupport,
    supportEmail,
    supportPhoneNumber,
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function addAgreement(locationForUserAgreement, text) {
  return axios.post("/api/private/admin/addAndUpdateUserAgreementText", {
    locationForUserAgreement,
    text,
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function getQuestion(location) {
  return axios.post(
    `/api/public/v1/getPopularQuestions?${"location=" + location}`
  );
}

export function AddQuestion(locationForPopularQuestion, answer, question) {
  return axios.post("/api/private/admin/addAndUpdatePopularQuestions", {
    locationForPopularQuestion,
    answer: [answer],
    question,
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function GetTariff() {
  return axios.get("/api/private/admin/getTariffText");
}

export function AddTarifText(locationForTariff, text) {
  return axios.post("/api/private/admin/addAndUpdateTariffText", {
    locationForTariff,
    text,
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function addTatiffs(
  nameTariffGroup,
  period,
  price,
  tariffName,
  typeTime,
  workingTimeInterval
) {
  return axios.post(
    `/api/private/admin/addTariff?${"nameTariffGroup=" + nameTariffGroup}`,
    {
      period,
      price,
      tariffName,
      typeTime,
      workingTimeInterval,
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    }
  );
}

export function addTariffGroup(nameGroupTariffs, location, TariffType) {
  return axios.get(
    `/api/private/admin/addGroupTariffs?${
      "nameGroupTariffs=" + nameGroupTariffs
    }&${"location=" + location}&${"type=" + TariffType}`,
    {
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    }
  );
}

/** Like addTariffGroup but with optional holdAmount and penaltyAmount (if backend supports them). */
export function addTariffGroupWithHoldPenalty(
  nameGroupTariffs,
  location,
  TariffType,
  holdAmount,
  penaltyAmount
) {
  let url = `/api/private/admin/addGroupTariffs?nameGroupTariffs=${encodeURIComponent(nameGroupTariffs)}&location=${encodeURIComponent(location)}&type=${encodeURIComponent(TariffType)}`;
  if (holdAmount != null && holdAmount !== '' && Number.isFinite(Number(holdAmount))) {
    url += `&holdAmount=${encodeURIComponent(Number(holdAmount))}`;
  }
  if (penaltyAmount != null && penaltyAmount !== '' && Number.isFinite(Number(penaltyAmount))) {
    url += `&penaltyAmount=${encodeURIComponent(Number(penaltyAmount))}`;
  }
  return axios.get(url, {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function BlockRent(rentId) {
  return axios.get("/api/private/admin/finishRent", {
    params: {
      rentId,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function DeleteQuestion(i) {
  const id = i[0];
  return axios.get("/api/private/admin/deletePopularQuestion", {
    params: {
      id,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function Transactions(page, pageSize) {
  const params = {
    page,
    ...(pageSize != null && { pageSize }),
  };
  // Read role per call — module-scope Role was stale (null before login) and
  // sent SUPER_ADMIN to getTransactions → BE NPE.
  const role = localStorage.getItem("role");
  if (role === "SUPER_ADMIN") {
    return axios.get("/api/private/admin/allTransactions", {
      params,
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    });
  }
  return axios.get("/api/private/admin/getTransactions", {
    params,
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function addTransActions(
  phoneNumber,
  description,
  price,
  rentId,
  transactionStatus
) {
  return axios.post(`/api/private/admin/addTransaction${phoneNumber}`, {
    description,
    price,
    rentId,
    transactionStatus,
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function getMerchants(all) {
  return axios.get(`/api/private/admin/getMerchants?all=${all}`, {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}
export function getMerchantsRequest() {
  return axios.get("/api/private/admin/getMerchantsRequest", {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}
export function getMerchantsRequestFullInfo(email) {
  return axios.get(
    `/api/private/admin/getMerchantFullInfo?${"email=" + email}`,
    {
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    }
  );
}

export function getAdmins() {
  return axios.get("/api/private/admin/getAdmins", {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function addMerchants(adminEmail, login, password) {
  return axios.post(
    "/api/private/admin/regMerchant",
    { adminEmail, login, password },
    {
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    }
  );
}

export function addAdmins(login, password) {
  return axios.post("/api/private/admin/regAdmin", {
    login,
    password,
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function getMerchantsBy(adminEmail) {
  return axios.get(
    `/api/private/admin/getMerchantsByAdmin?${"adminEmail=" + adminEmail}`,
    {
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    }
  );
}

export function lockMerchant(email, lock) {
  return axios.get("/api/private/admin/lockMerchant", {
    params: { email, lock },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

/** Full merchant registration (legal entity). Public endpoint, may be called with admin token. */
export function requestRegMerchantLegalEntity(data) {
  return axios.post("/api/public/requestRegMerchantLegalEntity", data, {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

/** Full merchant registration (individual / self-employed). Public endpoint. */
export function requestRegMerchantIndividual(data) {
  return axios.post("/api/public/requestRegMerchantIndividual", data, {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function TakePowerBank(stationId, slotNumber) {
  return axios.post(
    // `/api/private/admin/getPowerBank?${"stationId=" + stationId}&${
    //   "slotNumber=" + slotNumber
    // }`,
    `/api/private/admin/powerbank/extract/${stationId}/${slotNumber}`
    // {
    //   headers: {
    //     authorization: "Bearer " + localStorage.getItem("token"),
    //   },
    // }
  );
}

export function FilterRentsByDate(data) {
  let startDate = data.StartDate;
  let endDate = data.EndDate;
  // Accept date-only from older callers; backend needs LocalDateTime pattern.
  if (startDate && /^\d{4}-\d{2}-\d{2}$/.test(String(startDate))) {
    startDate = `${startDate}T00:00:00.000`;
  }
  if (endDate && /^\d{4}-\d{2}-\d{2}$/.test(String(endDate))) {
    endDate = `${endDate}T23:59:59.999`;
  }
  return axiosInstance.get(`/api/private/admin/findRentByDate`, {
    timeout: 120000,
    params: {
      startDate,
      endDate,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function GetAllPowerbanks(page, size) {
  return axios.get("/api/private/admin/powerbank/", {
    params: {
      page,
      ...(size != null && { size }),
    },
  });
}

export function GetPowerbanksByStatus(page, status, checkingStatus) {
  return axios.get("/api/private/admin/powerbank/", {
    params: {
      page,
      status,
      checkingStatus,
    },
  });
}

export function GetPowerbanksDashboardInfo() {
  return axios.get("/api/private/admin/powerbank/dashboard");
}

export function GetPowerbanksDashboardInfoSortByMerchant(merchantEmail, partnerEmail) {
  return axios.get("/api/private/admin/powerbank/dashboard", {
    params: {
      merchantEmail,
      partnerEmail,
    },
  });
}

export function GetMerchantsDashboardInfo() {
  return axios.get("/api/private/admin/merchant/email");
}

export function GetLostPowerbanksInfo(startDate, endDate, merchant, station, partnerEmail) {
  return axios.get("/api/private/admin/dashboard/powerbank/lost", {
    params: {
      startDate,
      endDate,
      merchant,
      station,
      partnerEmail,
    },
  });
}

export function DeleteTariffApi(idGroupTariffs) {
  return axios.get("/api/private/admin/deleteGroupTariffs", {
    params: {
      idGroupTariffs,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function changePassword(password) {
  return axios.post(
    "/api/private/admin/changePassword",
    { password },
    {
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    }
  );
}

export function SentRepo(data) {
  const merchantEmail = data.email;
  const endDate = data.EndDate;
  const startedDate = data.StartDate;
  return axios.get(`/api/private/admin/sendReport`, {
    params: {
      merchantEmail,
      startedDate,
      endDate,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function FilterRentsByStatus(data) {
  return axios.get(`/api/private/admin/allRentsByStatus`, {
    params: {
      page: data[1],
      status: data[0],
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function GivePromoApi(data) {
  return axios.get(`/api/private/admin/specialTariffStatus`, {
    params: {
      userId: data,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function GetAllPromoTariffs() {
  return axios.get(`/api/private/admin/special-tariffs`, {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function GivePromoToUserApi(data) {
  return axios.post(`/api/private/admin/special-tariff`, null, {
    params: {
      userId: data[0],
      b: data[1],
      groupTariffId: data[2],
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    }
  });
}

export function DownloadStationList() {
  return axios.get(`/api/private/admin/allStationsDownload`, {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function FindTransactionByRentId(transactionId) {
  return axios.get("/api/private/admin/findTransactionByRentId", {
    params: {
      rentId: transactionId,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function editTransaction(transactionId, description, price, rentId, transactionStatus) {
  return axios.post(
    `/api/private/admin/editTransaction?transactionId=${encodeURIComponent(transactionId)}`,
    { description, price, rentId, transactionStatus },
    {
      headers: {
        authorization: "Bearer " + localStorage.getItem("token"),
      },
    }
  );
}

// export function getCommentsApi(name){
// return axios.get('/api/private/admin/findTransactionByRentId',{
//   params: {
//     'rentId':transactionId
//   },
//   headers: {
//     authorization: 'Bearer ' + localStorage.getItem('token'),
//   }
// })
// }

export function SetCommentsApi(text, name, id) {
  return axios.get("/api/private/admin/addComment", {
    params: {
      text: text,
      commentType: name,
      id: id,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

/** Append-only comments (author + time set server-side). */
export function commentUser(id, text) {
  return axios.post(
    `/api/private/admin/users/${encodeURIComponent(id)}/comments`,
    { text },
    { headers: { authorization: "Bearer " + localStorage.getItem("token") } },
  );
}

export function commentRent(id, text) {
  return axios.post(
    `/api/private/admin/rents/${encodeURIComponent(id)}/comments`,
    { text },
    { headers: { authorization: "Bearer " + localStorage.getItem("token") } },
  );
}

export function commentStation(id, text) {
  return axios.post(
    `/api/private/admin/stations/${encodeURIComponent(id)}/comments`,
    { text },
    { headers: { authorization: "Bearer " + localStorage.getItem("token") } },
  );
}

export function commentPowerbank(id, text) {
  return axios.post(
    `/api/private/admin/powerbank/${encodeURIComponent(id)}/comments`,
    { text },
    { headers: { authorization: "Bearer " + localStorage.getItem("token") } },
  );
}

export function ReloadStation(stationID) {
  return axios.get("/api/private/admin/restartStation", {
    params: {
      stationID: stationID,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

/** Find powerbank by human ID (e.g. BZYH…). Returns PowerBankVO with station/slot. */
export function FindPowerBank(powerBankId) {
  const id = encodeURIComponent(String(powerBankId ?? "").trim());
  return axios.get(`/api/private/admin/powerbank/${id}`, {
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

export function GetUserCardApi(userId) {
  return axios.get(`/api/private/admin/userCards`, {
    params: {
      userId,
    },
    headers: {
      authorization: "Bearer " + localStorage.getItem("token"),
    },
  });
}

// DASHBOARD MAP ====================================================

export function GetStationsOnMap() {
  return axios.get('/api/public/v2/map/stations');
}

export function GetStationInfoOnMap(stationId) {
  return axios.get(`/api/public/v2/map/station/${stationId}/full`);
}

// SERVICE MODULE ====================================================

export function getServiceTickets(params = {}) {
  return axios.get('/api/private/admin/service/tickets', { params });
}

export function getServiceTicket(id) {
  return axios.get(`/api/private/admin/service/tickets/${id}`);
}

export function createServiceTicket(body) {
  return axios.post('/api/private/admin/service/tickets', body);
}

export function assignServiceTicket(id, assigneeEmail) {
  return axios.post(`/api/private/admin/service/tickets/${id}/assign`, { assigneeEmail });
}

export function startServiceTicket(id) {
  return axios.post(`/api/private/admin/service/tickets/${id}/start`);
}

export function completeServiceTicket(id, body) {
  return axios.post(`/api/private/admin/service/tickets/${id}/complete`, body);
}

export function reviewServiceTicket(id, body) {
  return axios.post(`/api/private/admin/service/tickets/${id}/review`, body);
}

export function cancelServiceTicket(id, reason) {
  return axios.post(`/api/private/admin/service/tickets/${id}/cancel`, { reason });
}

export function commentServiceTicket(id, text) {
  return axios.post(`/api/private/admin/service/tickets/${id}/comments`, { text });
}

export function uploadServiceTicketPhoto(id, itemId, file) {
  const form = new FormData();
  form.append('itemId', itemId);
  // Keep filename for mobile (iOS may send empty name) — force one if missing.
  const rawName = (file && file.name) || '';
  const hasExt = /\.[a-z0-9]+$/i.test(rawName);
  const name = hasExt ? rawName : `photo_${Date.now()}.jpg`;
  const type = (file && file.type && file.type.startsWith('image/')) ? file.type : 'image/jpeg';
  let payload = file;
  if (file && (name !== file.name || type !== file.type)) {
    try {
      payload = new File([file], name, { type, lastModified: file.lastModified || Date.now() });
    } catch (_) {
      payload = file;
    }
  }
  form.append('file', payload, name);
  // Do NOT set Content-Type manually — browser must add multipart boundary.
  return axios.post(`/api/private/admin/service/tickets/${id}/photos`, form);
}

export function getServiceTicketPhotoUrl(ticketId, photoId) {
  return `/api/private/admin/service/tickets/${ticketId}/photos/${photoId}`;
}

export function getServiceTicketPhotoBlob(ticketId, photoId) {
  return axios.get(`/api/private/admin/service/tickets/${ticketId}/photos/${photoId}`, {
    responseType: 'blob',
  });
}

export function getServiceTechnicians(params = {}) {
  return axios.get('/api/private/admin/service/technicians', { params });
}

export function createServiceTechnician(body) {
  return axios.post('/api/private/admin/service/technicians', body);
}

export function patchServiceTechnician(id, body) {
  return axios.patch(`/api/private/admin/service/technicians/${id}`, body);
}

export function getStationServiceHistory(stationNumber, params = {}) {
  return axios.get(`/api/private/admin/stations/${stationNumber}/service-history`, { params });
}

export function getPowerbankServiceHistory(id, params = {}) {
  return axios.get(`/api/private/admin/powerbanks/${id}/service-history`, { params });
}
